import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AppCanvas } from "@/components/app/AppCanvas";
import { NextSceneCard } from "@/components/app/NextSceneCard";
import { CATALOG_SITUATIONS } from "@/data/catalogScenarios";
import { supabase } from "@/integrations/supabase/client";
import {
  LEVEL_LABELS,
  filterCatalog,
  findCatalogSituation,
  type CatalogLevel,
} from "@/lib/catalog";
import {
  FEATURED_MISSIONS,
  MISSION_LIFE_AXIS,
  findFeaturedMission,
  isFeaturedMissionId,
  missionProgressFromSessions,
  missionSlug,
  sceneMinutes,
} from "@/lib/missions";
import { UpgradeBanner } from "@/components/paywall";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { ListCard, ListRow } from "@/components/ui/list-card";
import { StatusDot } from "@/components/ui/status";

type SessionRow = {
  catalog_slug: string | null;
  analysis: unknown;
  status: string | null;
};

const Practice = () => {
  const { isPaid } = useSubscription();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState<SessionRow[]>([]);
  const [showCatalog, setShowCatalog] = useState(false);
  const missionId = searchParams.get("mission");
  const mission = isFeaturedMissionId(missionId) ? findFeaturedMission(missionId) : undefined;

  useEffect(() => {
    const load = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data } = await supabase
        .from("conversation_sessions")
        .select("catalog_slug, analysis, status")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(80);
      setRows((data || []) as SessionRow[]);
    };
    void load();
  }, []);

  const progress = useMemo(
    () => missionProgressFromSessions(rows, CATALOG_SITUATIONS),
    [rows],
  );

  const missionScene = mission
    ? findCatalogSituation(CATALOG_SITUATIONS, progress[mission.id].lastSlug ?? missionSlug(mission))
    : null;

  const rhythms = mission
    ? (["beginner", "intermediate", "advanced"] as CatalogLevel[])
        .map((level) => findCatalogSituation(CATALOG_SITUATIONS, missionSlug(mission, level)))
        .filter((item): item is NonNullable<typeof item> => Boolean(item))
    : [];

  const otherScenes = useMemo(
    () => filterCatalog(CATALOG_SITUATIONS, "all", "all").slice(0, 24),
    [],
  );

  const featuredByAxis = MISSION_LIFE_AXIS.map((axis) => ({
    ...axis,
    missions: axis.missionIds
      .map((id) => FEATURED_MISSIONS.find((item) => item.id === id)!)
      .filter(Boolean),
  }));

  return (
    <AppCanvas className="space-y-8">
      <div>
        <h1 className="text-h1 text-neutral-900">Praticar</h1>
        <p className="mt-1 text-body text-neutral-500">
          Escolhe a cena da vida. O nível fica dentro da missão.
        </p>
        <UpgradeBanner className="mt-4" />
      </div>

      {mission && missionScene && (
        <div className="space-y-4">
          <NextSceneCard
            situation={missionScene}
            ctaLabel={isPaid ? "Ensaiar agora" : "Assinar para ensaiar"}
            to={isPaid ? undefined : "/app/upgrade"}
            summaryLine={
              progress[mission.id].handled && progress[mission.id].stuck
                ? `Se virou em ${progress[mission.id].handled} · Travou em ${progress[mission.id].stuck}`
                : progress[mission.id].handled
                  ? `Se virou em ${progress[mission.id].handled}`
                  : null
            }
          />
          <div className="flex flex-wrap gap-2">
            {rhythms.map((scene) => (
              <Link
                key={scene.slug}
                to={`/app/practice/${scene.slug}`}
                className="inline-flex h-8 items-center rounded-pill border border-neutral-200 bg-white px-3 text-label text-neutral-700"
              >
                {LEVEL_LABELS[scene.level]}
              </Link>
            ))}
          </div>
        </div>
      )}

      {!mission && featuredByAxis.map((axis) => (
        <section key={axis.id} className="space-y-3">
          <h2 className="text-overline text-neutral-500">{axis.label}</h2>
          <ListCard>
            {axis.missions.map((item) => {
              const scene = findCatalogSituation(CATALOG_SITUATIONS, progress[item.id].lastSlug ?? missionSlug(item));
              const count = progress[item.id].rehearsals;
              return (
                <ListRow
                  key={item.id}
                  to={`/app/practice?mission=${item.id}`}
                  title={item.name}
                  subtitle={`${scene?.title} · ${scene ? sceneMinutes(scene) : ""} min · ${
                    count > 0 ? `já ensaiou ${count} vez${count === 1 ? "" : "es"}` : "ainda não ensaiou"
                  }`}
                  status={<StatusDot status={progress[item.id].status} />}
                />
              );
            })}
          </ListCard>
        </section>
      ))}

      <button
        type="button"
        onClick={() => {
          setShowCatalog((open) => !open);
          if (mission) {
            const next = new URLSearchParams(searchParams);
            next.delete("mission");
            setSearchParams(next, { replace: true });
          }
        }}
        className="text-body-sm text-blue-600"
      >
        Ver outras cenas
      </button>

      {showCatalog && (
        <ListCard>
          {otherScenes.map((situation) => (
            <ListRow
              key={situation.slug}
              to={`/app/practice/${situation.slug}`}
              title={situation.title}
              subtitle={`${LEVEL_LABELS[situation.level]} · ${situation.estimatedMinutes} min`}
            />
          ))}
        </ListCard>
      )}
    </AppCanvas>
  );
};

export default Practice;
